import { useCallback, useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { API_ACTIVITY_EVENT } from '../utils/ApiClient';
import { toast } from 'react-toastify';
import { SESSION_EXPIRED_EVENT } from '../resources/api.resource';

const WARNING_TOAST_ID = 'inactivity-warning';
const DEFAULT_SESSION_TIMEOUT_MINUTES = 120;
const getEnv = (key: string): string | undefined =>
  (window as typeof window & { _env_?: Record<string, string> })._env_?.[key] ?? process.env[key];

const configuredSessionTimeoutMinutes = parseInt(
  getEnv('REACT_APP_SESSION_TIMEOUT_MINUTES') || '',
  10
);
const SESSION_TIMEOUT_MS =
  (Number.isFinite(configuredSessionTimeoutMinutes) && configuredSessionTimeoutMinutes > 0
    ? configuredSessionTimeoutMinutes
    : DEFAULT_SESSION_TIMEOUT_MINUTES) * 60_000;
const PROMPT_BEFORE_MS = 5 * 60_000;

const useInactivityTimer = (isAuthenticated: boolean): void => {
  const onPrompt = useCallback(() => {
    toast.warning(
      'You are about to be logged off in 5 minutes due to inactivity.',
      {
        toastId: WARNING_TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        position: 'top-center',
      }
    );
  }, []);

  const onActive = useCallback(() => {
    toast.dismiss(WARNING_TOAST_ID);
  }, []);

  const onIdle = useCallback(() => {
    toast.dismiss(WARNING_TOAST_ID);
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }, []);

  const { activate } = useIdleTimer({
    timeout: SESSION_TIMEOUT_MS,
    promptBeforeIdle: PROMPT_BEFORE_MS,
    disabled: !isAuthenticated,
    crossTab: true,
    name: 'inactivity-timer',
    stopOnIdle: true,
    events: [],
    onPrompt,
    onActive,
    onIdle,
  });

  useEffect(() => {
    const handleApiActivity = () => activate();
    window.addEventListener(API_ACTIVITY_EVENT, handleApiActivity);
    return () => window.removeEventListener(API_ACTIVITY_EVENT, handleApiActivity);
  }, [activate]);
};

export default useInactivityTimer;
